import './index.scss';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Toast from '../../shared/Toast/Toast';
import { useTranslation } from 'react-i18next';
import { Button, Form, ModalFooter, ModalHeader } from 'reactstrap';
import { ModalComponentEnum } from '../../../store/modal/modalTypes';
import { useFetchProfile, useProfile } from '../../../store/Auth/authHooks';
import {
  useCloseOldModal,
  useOpenOldModal,
} from '../../../store/modal/modalHooks';
import { MedicalFormPartA } from '../../pages/gym-medical-form/MedicalFormPartA';
import { MedicalFormPartB } from '../../pages/gym-medical-form/MedicalFormPartB';
import { MedicalFormPartC } from '../../pages/gym-medical-form/MedicalFormPartC';
import { MedicalFormFooter } from '../../pages/gym-medical-form/MedicalFormFooter';
import { useSendMedicalDeclaration } from '../../../store/gym-management/GymHooks';
import { sendMedicalDeclarationDto } from '../../../store/gym-management/GymManagementDto';
import MedicalFormSuccessMessage from '../../pages/gym-medical-form/MedicalFormSuccessMessage';
import { MedicalDeclarationFormValues } from '../../../store/gym-management/GymManagementInterfaces';
import { MedicalFormGeneralInformation } from '../../pages/gym-medical-form/MedicalFormGeneralInformation';
import { SendMedicalDeclarationRequestDto } from '../../../API/gym/dto/request/send-medical-declaration.request.dto';
import { useGetLocation } from '../../../store/RoomManagement/resourceManagementHooks';

const MedicalFormDeclaration = () => {
  const profile = useProfile();
  const { t } = useTranslation();
  const location = useGetLocation();
  const openModal = useOpenOldModal();
  const closeModal = useCloseOldModal();
  const fetchProfile = useFetchProfile();
  const sendMedicalDeclaration = useSendMedicalDeclaration();

  const [showSuccessPage, setShowSuccessPage] = useState(false);

  const successMessageStatus = 'MEDICAL_FORM_VALID_SUCCESS_MESSAGE_CONTENT';

  const defaultValues: MedicalDeclarationFormValues = {
    fullName: profile.fullName || '',
    idNumber: profile.idNumber || '',
    age: profile.age || '',
    heartDisease: '',
    restChestPain: '',
    dailyChestPain: '',
    exerciseChestPain: '',
    lostBalance: '',
    lostConsciousness: '',
    asthmaMedicalCare: '',
    asthmaShortnessBreath: '',
    familyMemberHeartDisease: '',
    familyMemberSuddenDeath: '',
    trainingUnderMedical: '',
    chronicDisease: '',
    pregnancy: '',
    signature: false,
    childFullName: '',
    parentName: '',
    childIdNumber: '',
    trainingTypes: [],
    parentDeclarationDate: '',
    parentSignature: '',
    didGymTour: false,
    isAgreeTerms: false,
  };

  const form = useForm({ defaultValues });
  const {
    handleSubmit,
    formState: {},
  } = form;

  const onSubmit = (data: MedicalDeclarationFormValues) => {
    const haveDoneGymTour = data.didGymTour;
    const newMedicalDeclaration: SendMedicalDeclarationRequestDto =
      sendMedicalDeclarationDto(profile, data);

    const sendMedical = (didGymTour?: boolean) => {
      const medicalQuestionnaire =
        newMedicalDeclaration.form.medical_questionnaire;

      for (const [key, value] of Object.entries(medicalQuestionnaire)) {
        if (value) {
          sendMedicalDeclaration(newMedicalDeclaration, didGymTour).then(() => {
            fetchProfile().finally();
            openModal(ModalComponentEnum.UPLOAD_MEDICAL_CERTIFICATE_MODAL, {
              isFullSizeOnMobile: true,
            });
          });

          return;
        }
      }

      sendMedicalDeclaration(newMedicalDeclaration, didGymTour)
        .then(() => {
          onSuccess();
        })
        .catch((e) => {
          onFailure(e.message);
        });
    };
    sendMedical(haveDoneGymTour);
  };

  const onSuccess = () => {
    fetchProfile().finally();
    setShowSuccessPage(true);
  };

  const onFailure = (errorMessage: string) => {
    const toastType = 'error';
    Toast(toastType, errorMessage, t);
  };

  return (
    <div className="medical-form-declaration-modal" dir={'rtl'}>
      <div className="form-modal">
        <Form onSubmit={handleSubmit(onSubmit)}>
          {!showSuccessPage && (
            <ModalHeader>
              <div className="form-header-title">
                <p>{t('MEDICAL_DECLARATION_TITLE')}</p>
              </div>
            </ModalHeader>
          )}
          {!showSuccessPage && (
            <>
              <MedicalFormGeneralInformation form={form} />
              <MedicalFormPartA
                form={form}
                gymTourQuestion={location.isGymAutoFillEnabled}
              />
              <MedicalFormPartB />
              <MedicalFormPartC form={form} />
              <MedicalFormFooter form={form} />
            </>
          )}
          {showSuccessPage && (
            <MedicalFormSuccessMessage
              successMessage={successMessageStatus}
              noGymTour={profile.medicalForm?.didGymTour}
            />
          )}
          {!showSuccessPage && (
            <ModalFooter className="medical-form-declaration-modal-buttons">
              <Button
                type="button"
                color=""
                onClick={closeModal}
                className="declaration-modal-buttons-cancel"
              >
                {t('MEDICAL_DECLARATION_CANCEL_BUTTON_TEXT')}
              </Button>{' '}
              <Button type="submit" color="primary">
                {t('MEDICAL_DECLARATION_SEND_BUTTON_TEXT')}
              </Button>{' '}
            </ModalFooter>
          )}
        </Form>
      </div>
    </div>
  );
};

export default MedicalFormDeclaration;
